// Agents: each agent has an invisible Object3D "rig" (joints only); every body part type is ONE
// InstancedMesh per floor whose instance matrices are copied from the rigs each frame. This keeps
// draw calls constant (≈ 16 per floor) whatever the number of agents.
import { Color, InstancedMesh, Object3D, Sprite, Vector3, DynamicDrawUsage } from 'three';
import { BODY } from './geometries.js';
import { damp, dampAngle, solveTwoBone, wrapAngle, clamp, ease } from './anim.js';
import { rng } from './layout.js';
import { luminance, darken, mixHex } from './palette.js';

const CAMERA_YAW = Math.PI / 4;
const EYE_COLOR = '#2A211B';
const STATUSES = new Set(['idle', 'working', 'waiting', 'error', 'done']);
/** Seconds a rig keeps asking for frames after a status change (time to reach the new pose). */
const SETTLE_S = 0.8;

/** Part types → shared geometry key. */
const PARTS = ['torso', 'head', 'eye', 'hairCap', 'hairLong', 'bun', 'curly', 'cap', 'headphones',
  'upperArm', 'foreArm', 'hand', 'thigh', 'shin', 'shoe'];

/** Normalises an incoming status. */
export function normalizeStatus(s) {
  return STATUSES.has(s) ? s : 'idle';
}

class Rig {
  /**
   * @param {object} info {agent, look, seatX, seatZ, rot, podTop, deskIndex}
   */
  constructor(info) {
    this.info = info;
    this.id = info.agent.id;
    const look = info.look;
    this.rand = rng(look.seed ^ 0x9e3779b9);
    const B = BODY;
    this.root = new Object3D();
    this.root.position.set(info.seatX, info.podTop, info.seatZ);
    this.root.rotation.y = info.rot;
    this.hips = add(this.root, 0, B.hipSeated, 0);
    this.spine = add(this.hips, 0, 0, 0);
    this.torso = add(this.spine, 0, B.torsoY, 0);
    this.torso.scale.set(1.06, 1, 0.86);
    this.neck = add(this.spine, 0, B.neckY, 0);
    this.neck.rotation.order = 'YXZ';
    this.head = add(this.neck, 0, B.headY, 0);
    this.eyeL = add(this.head, 0.072, 0.02, 0.178);
    this.eyeR = add(this.head, -0.072, 0.02, 0.178);
    this.eyeL.scale.set(1, 1.35, 0.7);
    this.eyeR.scale.set(1, 1.35, 0.7);
    this.hairCap = add(this.head, 0, 0.012, -0.016);
    this.hairCap.rotation.x = -0.32;
    this.hairLong = add(this.head, 0, -0.14, -0.1);
    this.bun = add(this.head, 0, 0.15, -0.15);
    this.curly = add(this.head, 0, 0, 0);
    this.cap = add(this.head, 0, 0.012, -0.01);
    this.cap.rotation.x = -0.14;
    this.headphones = add(this.head, 0, 0.01, -0.01);
    this.arms = [1, -1].map((side) => {
      const shoulder = add(this.spine, side * B.shoulderX, B.shoulderY, 0);
      shoulder.rotation.order = 'ZYX';
      const upper = add(shoulder, 0, 0, 0);
      const elbow = add(shoulder, 0, -B.upperArm, 0);
      const fore = add(elbow, 0, 0, 0);
      const hand = add(elbow, 0, -B.foreArm, 0);
      return { side, shoulder, upper, elbow, fore, hand, a1: -0.6, a2: -0.9, roll: 0.1 };
    });
    this.legs = [1, -1].map((side) => {
      const hip = add(this.hips, side * B.hipX, 0, 0);
      const thigh = add(hip, 0, 0, 0);
      const knee = add(hip, 0, -B.thigh, 0);
      const shin = add(knee, 0, 0, 0);
      const shoe = add(knee, 0, -B.shin, 0);
      return { hip, thigh, knee, shin, shoe };
    });

    // Animation state.
    this.status = 'idle';
    this.statusAt = 0;
    this.stand = 0;
    this.lean = 0.04;
    this.yaw = info.rot;
    this.headYaw = 0;
    this.headPitch = 0;
    this.breathPhase = this.rand() * Math.PI * 2;
    this.breathRate = 1.6 + this.rand() * 0.6;
    this.typeRate = 15 + this.rand() * 6;
    this.nextGlance = 1 + this.rand() * 6;
    this.glanceUntil = 0;
    this.glanceYaw = 0;
    this.glancePitch = 0;
    this.nextStretch = 18 + this.rand() * 50;
    this.stretchUntil = -1;
    this.nextWave = 0.6 + this.rand() * 1.5;
    this.waveUntil = -1;
    this.nextGlyph = this.rand();
    this.screenOn = 0;
    this.doneUntil = -1;
    this.chairPush = 0;
    this.mouseHand = this.rand() < 0.5;
  }

  setStatus(status, time) {
    const s = normalizeStatus(status);
    if (s === this.status && s !== 'done') return;
    this.status = s;
    this.statusAt = time;
    if (s === 'done') this.doneUntil = time + 2.4;
  }

  /**
   * Advances the pose. Returns true when the agent is doing more than idle breathing.
   * @param {number} dt seconds
   * @param {number} time seconds
   * @param {{reducedMotion:boolean, emit:Function}} ctx
   */
  update(dt, time, ctx) {
    const B = BODY;
    const rm = ctx.reducedMotion;
    if (this.status === 'done' && time > this.doneUntil) {
      this.status = 'idle';
      this.statusAt = time;
    }
    const st = this.status;
    const tIn = time - this.statusAt;
    let standT = 0;
    let lean = 0.04;
    let turn = 0;
    let headYaw = 0;
    let headPitch = 0.04;
    let screen = 0;
    let arms = 'rest';

    if (st === 'working') {
      lean = 0.15;
      headPitch = 0.12 + (rm ? 0 : Math.sin(time * 0.7 + this.breathPhase) * 0.03);
      headYaw = rm ? 0 : Math.sin(time * 0.43 + this.breathPhase) * 0.12;
      screen = 1;
      arms = 'type';
    } else if (st === 'waiting') {
      standT = 1;
      lean = 0;
      turn = 0.72;
      headPitch = -0.06;
      screen = 0.55;
      arms = 'stand';
      if (!rm && time > this.nextWave) {
        this.waveUntil = time + 1.3;
        this.nextWave = time + 3.2 + this.rand() * 2.5;
      }
      if (time < this.waveUntil) arms = 'wave';
    } else if (st === 'error') {
      lean = 0.28;
      headPitch = 0.34;
      screen = 0.8;
      arms = 'head';
    } else if (st === 'done') {
      standT = tIn < 1.9 ? 1 : 0;
      turn = 0.45;
      headPitch = -0.18;
      screen = 0.4;
      arms = tIn < 1.7 ? 'cheer' : 'rest';
    } else {
      // Idle life: glances, a stretch now and then.
      if (!rm) {
        if (time > this.nextGlance) {
          this.glanceYaw = (this.rand() - 0.5) * 1.3;
          this.glancePitch = (this.rand() - 0.4) * 0.25;
          this.glanceUntil = time + 0.8 + this.rand() * 2;
          this.nextGlance = this.glanceUntil + 2 + this.rand() * 6;
        }
        if (time < this.glanceUntil) {
          headYaw = this.glanceYaw;
          headPitch = this.glancePitch;
        }
        if (time > this.nextStretch && this.stretchUntil < time) {
          this.stretchUntil = time + 3.2;
          this.nextStretch = time + 30 + this.rand() * 70;
        }
        if (time < this.stretchUntil) {
          const k = this.stretchUntil - time;
          standT = k > 0.5 ? 1 : 0;
          arms = k > 0.9 && k < 2.8 ? 'stretch' : 'stand';
          headPitch = -0.15;
          lean = 0;
        }
      }
    }

    const rate = rm ? 30 : 6;
    this.stand = damp(this.stand, standT, rate * 0.8, dt);
    this.lean = damp(this.lean, lean, rate, dt);
    const targetYaw = this.info.rot + wrapAngle(CAMERA_YAW - this.info.rot) * turn;
    this.yaw = dampAngle(this.yaw, targetYaw, rate * 0.7, dt);
    this.headYaw = dampAngle(this.headYaw, headYaw, rate, dt);
    this.headPitch = damp(this.headPitch, headPitch, rate, dt);
    this.screenOn = damp(this.screenOn, screen, rm ? 30 : 5, dt);
    this.chairPush = damp(this.chairPush, this.stand * 0.32, rate, dt);

    // Body.
    const s = this.stand;
    const breath = rm ? 0 : Math.sin(time * this.breathRate + this.breathPhase);
    const bounce = st === 'done' && arms === 'cheer' && !rm ? Math.abs(Math.sin(tIn * 9)) * 0.06 : 0;
    this.root.rotation.y = this.yaw;
    this.hips.position.set(0, B.hipSeated + (B.hipStanding - B.hipSeated) * s + bounce, -0.2 * s);
    this.spine.rotation.x = this.lean;
    this.spine.scale.set(1 + breath * 0.008, 1 + breath * 0.014, 1 + breath * 0.008);
    this.neck.rotation.set(this.headPitch, this.headYaw, 0, 'YXZ');
    for (const leg of this.legs) {
      leg.hip.rotation.x = -Math.PI / 2 * (1 - s);
      leg.knee.rotation.x = Math.PI / 2 * (1 - s);
    }

    // Arms (IK targets in spine space).
    for (const arm of this.arms) this.poseArm(arm, arms, time, dt, rm);

    // Particles while working.
    if (st === 'working' && !rm && ctx.emit && time > this.nextGlyph) {
      this.nextGlyph = time + 0.55 + this.rand() * 0.9;
      ctx.emit(this, 'glyph');
    }
    // Still moving towards the pose of a status that just changed.
    const settling = time - this.statusAt < SETTLE_S || Math.abs(this.stand - standT) > 0.01;
    // Reduced motion: every status is a still pose, so only settling (and the timed 'done' flash,
    // which must end by itself) needs frames; the render loop can then stop.
    if (rm) return settling || st === 'done';
    return st !== 'idle' || time < this.stretchUntil || settling;
  }

  poseArm(arm, mode, time, dt, rm) {
    const B = BODY;
    const sy = B.shoulderY;
    let ty = 0.37;
    let tz = 0.29;
    let roll = 0.08;
    const phase = arm.side > 0 ? 0 : Math.PI;
    switch (mode) {
      case 'type': {
        const tap = rm ? 0 : Math.max(0, Math.sin(time * this.typeRate + phase)) * 0.035;
        ty = 0.37 + tap;
        tz = 0.31 + (rm ? 0 : Math.sin(time * 1.3 + phase) * 0.018);
        roll = 0.1;
        break;
      }
      case 'rest': {
        if (this.mouseHand && arm.side < 0) {
          tz = 0.32 + (rm ? 0 : Math.sin(time * 0.9 + this.breathPhase) * 0.02);
          roll = 0.3;
        }
        break;
      }
      case 'stand':
        ty = sy - 0.39;
        tz = 0.05;
        roll = 0.13;
        break;
      case 'wave':
        if (arm.side < 0) {
          ty = sy + 0.28;
          tz = 0.12;
          roll = 0.45 + (rm ? 0 : Math.sin(time * 11) * 0.28);
        } else {
          ty = sy - 0.39;
          tz = 0.05;
          roll = 0.13;
        }
        break;
      case 'stretch':
        ty = sy + 0.4;
        tz = -0.02;
        roll = 0.2;
        break;
      case 'cheer':
        ty = sy + 0.34;
        tz = 0.08;
        roll = 0.62;
        break;
      case 'head':
        ty = B.neckY + 0.12;
        tz = 0.12;
        roll = 0.62;
        break;
      default:
        break;
    }
    const sol = solveTwoBone(sy, 0, ty, tz, B.upperArm, B.foreArm);
    const rate = rm ? 40 : 10;
    arm.a1 = dampAngle(arm.a1, sol.a1, rate, dt);
    arm.a2 = dampAngle(arm.a2, sol.a2, rate, dt);
    arm.roll = damp(arm.roll, roll, rate, dt);
    arm.shoulder.rotation.set(arm.a1, 0, arm.roll * arm.side, 'ZYX');
    arm.elbow.rotation.x = arm.a2;
  }
}

function add(parent, x, y, z) {
  const o = new Object3D();
  o.position.set(x, y, z);
  parent.add(o);
  return o;
}

/**
 * All agents of one floor.
 */
export class Crowd {
  /**
   * @param {{entries:Array<{agent:object, look:object, seatX:number, seatZ:number, rot:number,
   *   podTop:number, deskIndex:number, deptColor:string}>, geos:object, mats:object, parent:Object3D}} opts
   */
  constructor({ entries, geos, mats, parent }) {
    this.parent = parent;
    this.mats = mats;
    this.rigs = entries.map((e) => new Rig(e));
    this.byId = new Map(this.rigs.map((r) => [r.id, r]));
    this.meshes = {};
    this.slots = {};
    for (const p of PARTS) this.slots[p] = [];
    const color = new Color();
    for (const rig of this.rigs) {
      const L = rig.info.look;
      const shortSleeves = (L.seed >>> 5) % 3 === 0;
      const pants = mats.tokens.pants[L.pantsIndex % mats.tokens.pants.length];
      const hpColor = luminance(L.accent) > 0.7 ? '#3A3A40' : L.accent;
      const push = (part, slot, hex) => this.slots[part].push({ rig, slot, hex });
      push('torso', rig.torso, L.shirt);
      push('head', rig.head, L.skin);
      push('eye', rig.eyeL, EYE_COLOR);
      push('eye', rig.eyeR, EYE_COLOR);
      const style = L.hairStyle;
      if (style !== 'cap') push('hairCap', rig.hairCap, L.hair);
      if (style === 'long') push('hairLong', rig.hairLong, L.hair);
      if (style === 'bun') push('bun', rig.bun, L.hair);
      if (style === 'curly') push('curly', rig.curly, L.hair);
      if (style === 'cap') push('cap', rig.cap, capColor(L));
      if (L.headphones) push('headphones', rig.headphones, hpColor);
      for (const arm of rig.arms) {
        push('upperArm', arm.upper, L.shirt);
        push('foreArm', arm.fore, shortSleeves ? L.skin : L.shirt);
        push('hand', arm.hand, L.skin);
      }
      for (const leg of rig.legs) {
        push('thigh', leg.thigh, pants);
        push('shin', leg.shin, pants);
        push('shoe', leg.shoe, darken(pants, 0.45));
      }
    }
    for (const p of PARTS) {
      const list = this.slots[p];
      if (!list.length) continue;
      const mesh = new InstancedMesh(geos[p], mats.body, list.length);
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      list.forEach((s, i) => mesh.setColorAt(i, color.set(s.hex)));
      mesh.instanceColor.needsUpdate = true;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      mesh.name = `agents-${p}`;
      parent.add(mesh);
      this.meshes[p] = mesh;
    }
    this.bubbles = new Map();
    this.time = 0;
    this.writeMatrices();
  }

  /** Sets an agent's status (unknown ids are ignored). */
  setStatus(agentId, status, time) {
    const rig = this.byId.get(agentId);
    if (!rig) return false;
    rig.setStatus(status, time);
    return true;
  }

  /** Current status of an agent (after the 'done' flash has ended it is 'idle'). */
  statusOf(agentId) {
    return this.byId.get(agentId)?.status ?? null;
  }

  /**
   * Advances every rig and uploads the matrices.
   * @returns {boolean} true while at least one agent does more than breathing
   */
  update(dt, time, ctx) {
    this.time = time;
    let active = false;
    for (const rig of this.rigs) {
      if (rig.update(dt, time, ctx)) active = true;
    }
    this.writeMatrices();
    this.updateBubbles(time, ctx);
    return active;
  }

  writeMatrices() {
    for (const rig of this.rigs) rig.root.updateMatrixWorld(true);
    for (const p of PARTS) {
      const mesh = this.meshes[p];
      if (!mesh) continue;
      const list = this.slots[p];
      for (let i = 0; i < list.length; i++) mesh.setMatrixAt(i, list[i].slot.matrixWorld);
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /** Floor-local position of the top of an agent's head. */
  headTop(rig, out = new Vector3()) {
    out.set(0, BODY.headR, 0).applyMatrix4(rig.head.matrixWorld);
    return out;
  }

  updateBubbles(time, ctx) {
    const scale = ctx.bubbleScale || 0.7;
    const rm = ctx.reducedMotion;
    const tmp = new Vector3();
    for (const rig of this.rigs) {
      const kind = rig.status === 'waiting' || rig.status === 'error' || rig.status === 'done' ? rig.status : null;
      let sprite = this.bubbles.get(rig.id);
      if (!kind) {
        if (sprite) {
          this.parent.remove(sprite);
          this.bubbles.delete(rig.id);
        }
        continue;
      }
      if (!sprite) {
        sprite = new Sprite(this.mats.bubbles[kind]);
        sprite.renderOrder = 20;
        sprite.userData.born = time;
        this.parent.add(sprite);
        this.bubbles.set(rig.id, sprite);
      }
      if (sprite.material !== this.mats.bubbles[kind]) {
        sprite.material = this.mats.bubbles[kind];
        sprite.userData.born = time;
      }
      const age = time - sprite.userData.born;
      let pop = rm ? 1 : ease.outBack(clamp(age / 0.35, 0, 1));
      let bob = 0;
      if (!rm && kind === 'waiting') bob = Math.abs(Math.sin(time * 3.2 + rig.breathPhase)) * 0.14;
      if (!rm && kind === 'error') pop *= 1 + Math.sin(time * 5) * 0.05;
      if (kind === 'done' && age > 1.9) pop *= clamp(1 - (age - 1.9) / 0.4, 0, 1);
      this.headTop(rig, tmp);
      // Beside the head (camera right) so the DOM name tag above the head does not hide it.
      sprite.position.set(tmp.x + 0.3, tmp.y + 0.12 + bob, tmp.z - 0.3);
      sprite.scale.setScalar(scale * Math.max(0.001, pop));
    }
  }

  /** Releases GPU resources owned by the crowd (geometries/materials are shared: not disposed). */
  dispose() {
    for (const mesh of Object.values(this.meshes)) {
      this.parent.remove(mesh);
      mesh.dispose();
    }
    for (const sprite of this.bubbles.values()) this.parent.remove(sprite);
    this.bubbles.clear();
  }

  /** Reapplies per-instance colours that depend on the theme (trousers). */
  applyTheme(tokens) {
    const color = new Color();
    for (const part of ['thigh', 'shin', 'shoe']) {
      const mesh = this.meshes[part];
      if (!mesh) continue;
      this.slots[part].forEach((s, i) => {
        const L = s.rig.info.look;
        const pants = tokens.pants[L.pantsIndex % tokens.pants.length];
        s.hex = part === 'shoe' ? darken(pants, 0.45) : pants;
        mesh.setColorAt(i, color.set(s.hex));
      });
      mesh.instanceColor.needsUpdate = true;
    }
  }
}

function capColor(look) {
  const a = look.accent;
  // A cap in the skin tone would read as a bald head: fall back to a mix with the shirt.
  if (Math.abs(luminance(a) - luminance(look.skin)) < 0.08) return mixHex(look.shirt, '#1D1B18', 0.3);
  return a;
}
